/**
 * i18n labels for stats: 72 keys for the metric registry + categories +
 * aggregation functions + chart types. The registry (`GET /v2/stats/metrics`)
 * has NO display name — `key` is technical (`rest_hr`), and the label lives
 * here. An unknown key (future metrics, custom nutrients) → falls back to
 * the key itself via `getMetricMessage`.
 */
import type { MessageDescriptor } from 'react-intl';

import { defineMessages } from 'react-intl';

export const metricMessages = defineMessages({
  activity_distance: {
    id: 'stats.metric.activity_distance',
    defaultMessage: 'Activity distance',
  },
  activity_duration_min: {
    id: 'stats.metric.activity_duration_min',
    defaultMessage: 'Activity duration',
  },
  activity_kcal: {
    id: 'stats.metric.activity_kcal',
    defaultMessage: 'Activity calories',
  },
  aov: {
    id: 'stats.metric.aov',
    defaultMessage: 'Average order value (AOV)',
  },
  arr: {
    id: 'stats.metric.arr',
    defaultMessage: 'ARR',
  },
  avg_sleep_hr: {
    id: 'stats.metric.avg_sleep_hr',
    defaultMessage: 'Average sleep heart rate',
  },
  blood_sugar: {
    id: 'stats.metric.blood_sugar',
    defaultMessage: 'Blood sugar',
  },
  blood_sugar_am: {
    id: 'stats.metric.blood_sugar_am',
    defaultMessage: 'Blood sugar (morning)',
  },
  blood_sugar_pm: {
    id: 'stats.metric.blood_sugar_pm',
    defaultMessage: 'Blood sugar (evening)',
  },
  bmi: {
    id: 'stats.metric.bmi',
    defaultMessage: 'BMI',
  },
  bmr: {
    id: 'stats.metric.bmr',
    defaultMessage: 'Basal metabolic rate (BMR)',
  },
  body_fat_kg: {
    id: 'stats.metric.body_fat_kg',
    defaultMessage: 'Body fat mass',
  },
  body_fat_pct: {
    id: 'stats.metric.body_fat_pct',
    defaultMessage: 'Body fat %',
  },
  bone_mass: {
    id: 'stats.metric.bone_mass',
    defaultMessage: 'Bone mass',
  },
  bp_diastolic: {
    id: 'stats.metric.bp_diastolic',
    defaultMessage: 'Blood pressure (diastolic)',
  },
  bp_diastolic_am: {
    id: 'stats.metric.bp_diastolic_am',
    defaultMessage: 'Blood pressure, diastolic (morning)',
  },
  bp_diastolic_pm: {
    id: 'stats.metric.bp_diastolic_pm',
    defaultMessage: 'Blood pressure, diastolic (evening)',
  },
  bp_systolic: {
    id: 'stats.metric.bp_systolic',
    defaultMessage: 'Blood pressure (systolic)',
  },
  bp_systolic_am: {
    id: 'stats.metric.bp_systolic_am',
    defaultMessage: 'Blood pressure, systolic (morning)',
  },
  bp_systolic_pm: {
    id: 'stats.metric.bp_systolic_pm',
    defaultMessage: 'Blood pressure, systolic (evening)',
  },
  caffeine_intake: {
    id: 'stats.metric.caffeine_intake',
    defaultMessage: 'Caffeine intake',
  },
  calories_burned: {
    id: 'stats.metric.calories_burned',
    defaultMessage: 'Calories',
  },
  carb_intake: {
    id: 'stats.metric.carb_intake',
    defaultMessage: 'Carbohydrate intake',
  },
  churn_rate: {
    id: 'stats.metric.churn_rate',
    defaultMessage: 'Churn rate',
  },
  circ_abdomen: {
    id: 'stats.metric.circ_abdomen',
    defaultMessage: 'Abdomen circumference',
  },
  circ_biceps: {
    id: 'stats.metric.circ_biceps',
    defaultMessage: 'Biceps circumference',
  },
  circ_calf: {
    id: 'stats.metric.circ_calf',
    defaultMessage: 'Calf circumference',
  },
  circ_chest: {
    id: 'stats.metric.circ_chest',
    defaultMessage: 'Chest circumference',
  },
  circ_forearm: {
    id: 'stats.metric.circ_forearm',
    defaultMessage: 'Forearm circumference',
  },
  circ_hips: {
    id: 'stats.metric.circ_hips',
    defaultMessage: 'Hips circumference',
  },
  circ_neck: {
    id: 'stats.metric.circ_neck',
    defaultMessage: 'Neck circumference',
  },
  circ_shoulders: {
    id: 'stats.metric.circ_shoulders',
    defaultMessage: 'Shoulders circumference',
  },
  circ_thigh: {
    id: 'stats.metric.circ_thigh',
    defaultMessage: 'Thigh circumference',
  },
  circ_waist: {
    id: 'stats.metric.circ_waist',
    defaultMessage: 'Waist circumference',
  },
  distance: {
    id: 'stats.metric.distance',
    defaultMessage: 'Daily distance',
  },
  ecw: {
    id: 'stats.metric.ecw',
    defaultMessage: 'Extracellular water',
  },
  effectiveness: {
    id: 'stats.metric.effectiveness',
    defaultMessage: 'Effectiveness',
  },
  fat_intake: {
    id: 'stats.metric.fat_intake',
    defaultMessage: 'Fat intake',
  },
  fibre_intake: {
    id: 'stats.metric.fibre_intake',
    defaultMessage: 'Fibre intake',
  },
  floors: {
    id: 'stats.metric.floors',
    defaultMessage: 'Floors',
  },
  height: {
    id: 'stats.metric.height',
    defaultMessage: 'Height',
  },
  hr_avg: {
    id: 'stats.metric.hr_avg',
    defaultMessage: 'Avg. daily heart rate',
  },
  hr_max: {
    id: 'stats.metric.hr_max',
    defaultMessage: 'Max. daily heart rate',
  },
  hr_min: {
    id: 'stats.metric.hr_min',
    defaultMessage: 'Min. daily heart rate',
  },
  hrv: {
    id: 'stats.metric.hrv',
    defaultMessage: 'Heart rate variability (HRV)',
  },
  icw: {
    id: 'stats.metric.icw',
    defaultMessage: 'Intracellular water',
  },
  kcal_intake: {
    id: 'stats.metric.kcal_intake',
    defaultMessage: 'Calorie intake',
  },
  kcal_intake_afternoon_snack: {
    id: 'stats.metric.kcal_intake_afternoon_snack',
    defaultMessage: 'Afternoon snack',
  },
  kcal_intake_breakfast: {
    id: 'stats.metric.kcal_intake_breakfast',
    defaultMessage: 'Breakfast',
  },
  kcal_intake_dinner: {
    id: 'stats.metric.kcal_intake_dinner',
    defaultMessage: 'Dinner',
  },
  kcal_intake_lunch: {
    id: 'stats.metric.kcal_intake_lunch',
    defaultMessage: 'Lunch',
  },
  kcal_intake_morning_snack: {
    id: 'stats.metric.kcal_intake_morning_snack',
    defaultMessage: 'Morning snack',
  },
  ltv: {
    id: 'stats.metric.ltv',
    defaultMessage: 'Customer lifetime value (LTV)',
  },
  max_hr: {
    id: 'stats.metric.max_hr',
    defaultMessage: 'Measured max. heart rate',
  },
  mineral_mass: {
    id: 'stats.metric.mineral_mass',
    defaultMessage: 'Mineral mass',
  },
  mood: {
    id: 'stats.metric.mood',
    defaultMessage: 'Mood',
  },
  morning_hr: {
    id: 'stats.metric.morning_hr',
    defaultMessage: 'Morning heart rate',
  },
  mrr: {
    id: 'stats.metric.mrr',
    defaultMessage: 'MRR',
  },
  muscle_mass_kg: {
    id: 'stats.metric.muscle_mass_kg',
    defaultMessage: 'Muscle mass',
  },
  muscle_mass_pct: {
    id: 'stats.metric.muscle_mass_pct',
    defaultMessage: 'Muscle mass %',
  },
  phase_angle: {
    id: 'stats.metric.phase_angle',
    defaultMessage: 'Phase angle',
  },
  protein_intake: {
    id: 'stats.metric.protein_intake',
    defaultMessage: 'Protein intake',
  },
  protein_mass: {
    id: 'stats.metric.protein_mass',
    defaultMessage: 'Protein mass',
  },
  rest_hr: {
    id: 'stats.metric.rest_hr',
    defaultMessage: 'Resting heart rate',
  },
  revenue: {
    id: 'stats.metric.revenue',
    defaultMessage: 'Revenue',
  },
  salt_intake: {
    id: 'stats.metric.salt_intake',
    defaultMessage: 'Salt intake',
  },
  sat_fat_intake: {
    id: 'stats.metric.sat_fat_intake',
    defaultMessage: 'Saturated fat intake',
  },
  session_avg_hr: {
    id: 'stats.metric.session_avg_hr',
    defaultMessage: 'Session avg. heart rate',
  },
  session_distance: {
    id: 'stats.metric.session_distance',
    defaultMessage: 'Session distance',
  },
  session_duration_min: {
    id: 'stats.metric.session_duration_min',
    defaultMessage: 'Session duration',
  },
  session_kcal: {
    id: 'stats.metric.session_kcal',
    defaultMessage: 'Session calories',
  },
  session_max_hr: {
    id: 'stats.metric.session_max_hr',
    defaultMessage: 'Session max. heart rate',
  },
  session_reps: {
    id: 'stats.metric.session_reps',
    defaultMessage: 'Session reps',
  },
  session_volume: {
    id: 'stats.metric.session_volume',
    defaultMessage: 'Session volume',
  },
  sleep_deep_min: {
    id: 'stats.metric.sleep_deep_min',
    defaultMessage: 'Deep sleep',
  },
  sleep_duration: {
    id: 'stats.metric.sleep_duration',
    defaultMessage: 'Sleep duration',
  },
  sleep_light_min: {
    id: 'stats.metric.sleep_light_min',
    defaultMessage: 'Light sleep',
  },
  sleep_rem_min: {
    id: 'stats.metric.sleep_rem_min',
    defaultMessage: 'REM sleep',
  },
  sleep_score: {
    id: 'stats.metric.sleep_score',
    defaultMessage: 'Sleep score',
  },
  sleep_wakeups: {
    id: 'stats.metric.sleep_wakeups',
    defaultMessage: 'Wake-ups',
  },
  steps: {
    id: 'stats.metric.steps',
    defaultMessage: 'Steps',
  },
  stress_level: {
    id: 'stats.metric.stress_level',
    defaultMessage: 'Stress level',
  },
  stress_overall: {
    id: 'stats.metric.stress_overall',
    defaultMessage: 'Overall stress',
  },
  sugar_intake: {
    id: 'stats.metric.sugar_intake',
    defaultMessage: 'Sugar intake',
  },
  tbw: {
    id: 'stats.metric.tbw',
    defaultMessage: 'Total body water',
  },
  training_level: {
    id: 'stats.metric.training_level',
    defaultMessage: 'Training level',
  },
  visceral_fat: {
    id: 'stats.metric.visceral_fat',
    defaultMessage: 'Visceral fat',
  },
  vo2max: {
    id: 'stats.metric.vo2max',
    defaultMessage: 'VO₂max',
  },
  water_intake: {
    id: 'stats.metric.water_intake',
    defaultMessage: 'Water intake',
  },
  weight: {
    id: 'stats.metric.weight',
    defaultMessage: 'Weight',
  },
  workout_count: {
    id: 'stats.metric.workout_count',
    defaultMessage: 'Workout count',
  },
});

export const metricCategoryMessages = defineMessages({
  activity: {
    id: 'stats.category.activity',
    defaultMessage: 'Activity',
  },
  body: {
    id: 'stats.category.body',
    defaultMessage: 'Body composition',
  },
  financial: {
    id: 'stats.category.financial',
    defaultMessage: 'Finance',
  },
  health: {
    id: 'stats.category.health',
    defaultMessage: 'Health',
  },
  nutrition: {
    id: 'stats.category.nutrition',
    defaultMessage: 'Nutrition',
  },
  performance: {
    id: 'stats.category.performance',
    defaultMessage: 'Performance',
  },
});

export const metricAggregationMessages = defineMessages({
  avg: {
    id: 'stats.aggregation.avg',
    defaultMessage: 'Average',
  },
  last: {
    id: 'stats.aggregation.last',
    defaultMessage: 'Last',
  },
  max: {
    id: 'stats.aggregation.max',
    defaultMessage: 'Maximum',
  },
  median: {
    id: 'stats.aggregation.median',
    defaultMessage: 'Median',
  },
  min: {
    id: 'stats.aggregation.min',
    defaultMessage: 'Minimum',
  },
  sum: {
    id: 'stats.aggregation.sum',
    defaultMessage: 'Sum',
  },
});

export const metricChartTypeMessages = defineMessages({
  bar: {
    id: 'stats.chartType.bar',
    defaultMessage: 'Bars',
  },
  donut: {
    id: 'stats.chartType.donut',
    defaultMessage: 'Donut',
  },
  line: {
    id: 'stats.chartType.line',
    defaultMessage: 'Line',
  },
  minmaxavg: {
    id: 'stats.chartType.minmaxavg',
    defaultMessage: 'Min–avg–max',
  },
  table: {
    id: 'stats.chartType.table',
    defaultMessage: 'Table',
  },
});

/** Metric label; unknown key → undefined (caller displays the key itself). */
export function getMetricMessage(key: string): MessageDescriptor | undefined {
  return (metricMessages as Record<string, MessageDescriptor>)[key];
}
